import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import {
  Search, Plus, X, ChevronRight, Building2, BookOpen,
  ExternalLink, FileText, Banknote, GripVertical, EyeOff, Pencil, Check, Info, AlertTriangle,
} from 'lucide-react';
import DropdownSelect from '../components/DropdownSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import MissingCodesModal from '../components/MissingCodesModal';
import { buildRenumberMappings } from './buildRenumberMappings';
import { useDateFilter } from '../contexts/DateFilterContext';
import { useToast } from '../components/Toast';
import { buildCoaTree } from '../lib/coa-hierarchy';

const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'other_income', 'cost', 'expense', 'other_expense'] as const;

const TYPE_LABELS: Record<string, string> = {
  asset: tr('Assets', '資產', '资产'),
  liability: tr('Liabilities', '負債', '负债'),
  equity: tr('Equity', '權益', '权益'),
  revenue: tr('Revenue', '收入', '收入'),
  other_income: tr('Other Income', '其他收入', '其他收入'),
  cost: tr('Cost', '直接成本', '直接成本'),
  expense: tr('Expenses', '支出', '支出'),
  other_expense: tr('Other Expenses', '其他支出', '其他支出'),
};

const TYPE_COLORS: Record<string, string> = {
  asset: 'bg-blue-50 text-black font-bold dark:bg-blue-900/30 dark:text-white',
  liability: 'bg-red-50 text-black font-bold dark:bg-red-900/30 dark:text-white',
  equity: 'bg-purple-50 text-black font-bold dark:bg-purple-900/30 dark:text-white',
  revenue: 'bg-green-50 text-black font-bold dark:bg-green-900/30 dark:text-white',
  other_income: 'bg-emerald-50 text-black font-bold dark:bg-emerald-900/30 dark:text-white',
  cost: 'bg-orange-50 text-black font-bold dark:bg-orange-900/30 dark:text-white',
  expense: 'bg-amber-50 text-black font-bold dark:bg-amber-900/30 dark:text-white',
  other_expense: 'bg-rose-50 text-black font-bold dark:bg-rose-900/30 dark:text-white',
};

function getDepth(code: string): number {
  if (!code) return 0;
  const stripped = code.replace(/0+$/, '');
  if (stripped.length <= 1) return 0;
  return Math.max(0, stripped.length - 1);
}

function isParentCode(code: string, allCodes: string[]): boolean {
  if (!code) return false;
  // Zero-stripped stem rule (same as backend guard + other pickers):
  // '66200' is a parent when another code starts with its stem '662'.
  // The old /00$/ heuristic missed parents like '11000'→'11' and misflagged
  // leaves that happen to end in 00.
  const stem = code.replace(/0+$/, '') || code.slice(0, 1);
  return allCodes.some(c => c !== code && c.startsWith(stem));
}

function formatBalance(v: number | null | undefined, forceZero = false): string {
  if (v == null) return '—';
  if (v === 0 && !forceZero) return '—';
  const abs = Math.abs(v);
  const formatted = abs.toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${formatted})` : formatted;
}

function getReferenceLabel(type: string | null): string {
  switch (type) {
    case 'bank_transaction': return tr('Bank', '銀行', '银行');
    case 'invoice': return tr('Invoice', '發票', '发票');
    case 'payment': return tr('Payment', '付款', '付款');
    case 'year_end_close': return tr('Year-End', '年結', '年结');
    default: return type || '';
  }
}

function getDepthBgClass(code: string, allCodes: string[]): string {
  if (!code) return '';
  if (isParentCode(code, allCodes)) {
    const depth = getDepth(code);
    if (depth === 0) return 'bg-slate-100';
    if (depth === 1) return 'bg-slate-50';
    if (depth === 2) return 'bg-white';
    return '';
  }
  // Leaf accounts
  return 'bg-white';
}

function getReferenceRoute(type: string | null): string {
  switch (type) {
    case 'bank_transaction': return '/bank-statements';
    // Invoice references are AP bills — the Expenses page dropped its invoice
    // tab (they display in AP/AR) on 2026-08-27.
    case 'invoice': return '/ap';
    case 'payment': return '/bank-statements';
    default: return '';
  }
}

export default function ChartOfAccounts() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [activeCoaType, setActiveCoaType] = useState<string | null>(null);

  useEffect(() => {
    if (!activeCoaType) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveCoaType(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [activeCoaType]);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  const [accountTxns, setAccountTxns] = useState<Record<string, any[]>>({});
  const [accountTxnLoading, setAccountTxnLoading] = useState<Record<string, boolean>>({});
  const [typeOrder, setTypeOrder] = useState<string[]>([...TYPE_ORDER]);
  const [hideZeroBalance, setHideZeroBalance] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [addFormParent, setAddFormParent] = useState<string | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState<{ code: string; name: string } | null>(null);
  const [editingName, setEditingName] = useState<{ code: string; value: string } | null>(null);
  const [editingOpeningState, setEditingOpeningState] = useState<{ code: string; value: string } | null>(null);
  const [newAccount, setNewAccount] = useState({
    account_code: '', account_name: '', account_type: 'asset', parent_code: '', opening_balance: 0,
  });
  const { startDate, endDate } = useDateFilter();
  const queryAsOf = endDate;

  const { data, isLoading } = useQuery({
    queryKey: ['accounts', queryAsOf],
    queryFn: () => {
      const base = queryAsOf ? `/bookkeeping/accounts?as_of=${queryAsOf}` : '/bookkeeping/accounts';
      const path = `${base}${base.includes('?') ? '&' : '?'}include_inactive=true`;
      return api(path) as Promise<{ data?: any[]; results?: any[] }>;
    },
    enabled: true,
  });

  const accounts = ((data as any)?.data || (data as any)?.results || []) as any[];
  const hasCurrentBalance = !!(data as any)?.as_of;
  const allCoaCodes = useMemo(() => accounts.map(a => a.account_code), [accounts]);

  // Build parent lookup for hierarchy (parent expansion / descendant hiding)
  const parentMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const a of accounts) {
      map.set(a.account_code, a.parent_code || null);
    }
    return map;
  }, [accounts]);

  const isDescendantHidden = useCallback((code: string): boolean => {
    if (collapsedParents.size === 0) return false;
    let current: string | null | undefined = parentMap.get(code);
    while (current) {
      if (collapsedParents.has(current)) return true;
      current = parentMap.get(current);
    }
    return false;
  }, [collapsedParents, parentMap]);

  const seedMut = useMutation({
    mutationFn: () => api('/bookkeeping/accounts/seed', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const createMut = useMutation({
    mutationFn: (body: typeof newAccount) => api('/bookkeeping/accounts', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setAddFormParent(null);
      setNewAccount({ account_code: '', account_name: '', account_type: 'asset', parent_code: '', opening_balance: 0 });
      toast.success(tr('Account created', '科目已建立', '科目已建立'));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const updateStatusMut = useMutation({
    mutationFn: ({ code, is_active }: { code: string; is_active: number }) =>
      api(`/bookkeeping/accounts/${code}`, { method: 'PATCH', body: { is_active } }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setConfirmDisable(null);
      toast.success(vars.is_active
        ? tr('Account enabled', '科目已啟用', '科目已启用')
        : tr('Account disabled', '科目已停用', '科目已停用'));
    },
    onError: (err: Error) => {
      setConfirmDisable(null);
      toast.error(err.message);
    },
  });

  const updateNameMut = useMutation({
    mutationFn: ({ code, account_name }: { code: string; account_name: string }) =>
      api(`/bookkeeping/accounts/${code}`, { method: 'PATCH', body: { account_name } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setEditingName(null);
      toast.success(tr('Account description updated', '科目描述已更新', '科目描述已更新'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateOpeningMut = useMutation({
    mutationFn: ({ code, opening_balance }: { code: string; opening_balance: number }) =>
      api(`/bookkeeping/accounts/${code}`, { method: 'PATCH', body: { opening_balance } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setEditingOpeningState(null);
      toast.success(tr('Opening balance updated', '期初結餘已更新', '期初结余已更新'));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const renumberMutation = useMutation({
    mutationFn: (mappings: { old_code: string; new_code: string }[]) =>
      api('/bookkeeping/accounts/renumber', { method: 'POST', body: { mappings } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      toast.success(tr('Codes re-arranged', '科目編碼已重新排列', '科目编码已重新排列'));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleRearrangeCodes = useCallback(() => {
    const mappings = buildRenumberMappings(accounts);
    if (mappings.length > 0) {
      renumberMutation.mutate(mappings);
    } else {
      toast.success(tr('Codes are already in order', '科目編碼已正確排列', '科目编码已正确排列'));
    }
  }, [accounts, renumberMutation, toast]);

  const { data: missingCodesData, isLoading: missingLoading } = useQuery({
    queryKey: ['missing-codes'],
    queryFn: () => api('/bookkeeping/accounts/missing-codes') as Promise<{ missing: any[]; total_existing: number; total_expected: number }>,
    enabled: accounts.length > 0,
    refetchOnWindowFocus: false,
  });
  const missingCodes = missingCodesData?.missing || [];

  // ── Drag-and-drop type reordering ──
  const handleDragStart = useCallback((index: number) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      setTypeOrder(prev => {
        const next = [...prev];
        const [dragged] = next.splice(dragIndex, 1);
        next.splice(index, 0, dragged);
        return next;
      });
      setDragIndex(index);
    }
  }, [dragIndex]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
  }, []);

  const toggleAccount = useCallback(async (code: string) => {
    if (isParentCode(code, allCoaCodes)) {
      // Parent account — toggle child visibility
      setCollapsedParents(prev => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code); else next.add(code);
        return next;
      });
    } else {
      // Leaf account — fetch transactions
      setExpandedAccounts(prev => ({ ...prev, [code]: !prev[code] }));
      if (!accountTxns[code] && !accountTxnLoading[code]) {
        setAccountTxnLoading(prev => ({ ...prev, [code]: true }));
        try {
          const sd = startDate || '2000-01-01';
          const ed = endDate || '2099-12-31';
          const res = await api(`/bookkeeping/accounts/${code}/transactions?start_date=${sd}&end_date=${ed}`);
          setAccountTxns(prev => ({ ...prev, [code]: (res as any).transactions || [] }));
        } catch {
          setAccountTxns(prev => ({ ...prev, [code]: [] }));
        }
        setAccountTxnLoading(prev => ({ ...prev, [code]: false }));
      }
    }
  }, [allCoaCodes, startDate, endDate, accountTxns, accountTxnLoading]);

  const handleAddChild = useCallback((parentCode: string, parentType: string) => {
    setNewAccount({
      account_code: '', account_name: '', account_type: parentType, parent_code: parentCode, opening_balance: 0,
    });
    setAddFormParent(parentCode);
    api(`/bookkeeping/accounts/next-code?parent_code=${parentCode}`)
      .then((data: any) => {
        if (data.next_code) {
          setNewAccount(p => ({ ...p, account_code: data.next_code }));
        }
      })
      .catch(() => {});
  }, []);

  const handleRefClick = (type: string | null, id: string | null) => {
    const route = getReferenceRoute(type);
    if (route) navigate(route);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const filtered = accounts.filter((a: any) => {
    if (isDescendantHidden(a.account_code)) return false;
    if (!showDisabled && a.is_active === 0) return false;
    if (typeFilter && a.account_type !== typeFilter) return false;
    if (hideZeroBalance && hasCurrentBalance && (!a.current_balance || Math.abs(a.current_balance) < 0.001)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.account_code || '').toLowerCase().includes(q) || (a.account_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const grouped: Record<string, any[]> = {};
  for (const t of typeOrder) grouped[t] = [];
  for (const a of filtered) {
    const t = a.account_type || 'expense';
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(a);
  }
  for (const t of Object.keys(grouped)) {
    grouped[t].sort((a: any, b: any) => (a.account_code || '').localeCompare(b.account_code || ''));
  }

  const hasAccounts = accounts.length > 0;

  const nonZeroByType: Record<string, number> = {};
  if (hasCurrentBalance) {
    for (const t of typeOrder) {
      nonZeroByType[t] = (grouped[t] || []).filter((a: any) => a.current_balance && Math.abs(a.current_balance) > 0.001).length;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">{tr('Chart of Accounts (COA)', '會計科目表', '会计科目表')}</h2>
          <p className="text-muted-foreground mt-1">
            {tr('5-digit tiered Hong Kong account structure.', '五位數分層香港會計科目結構。', '五位数分层香港会计科目结构。')}
          </p>
          <div className="mt-3 max-w-3xl rounded-xl border border-primary/25 bg-primary/5 p-3 text-sm text-foreground">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="font-semibold">{tr('Balance reporting note', '結餘報告說明', '余额报告说明')}</p>
                <p className="mt-1 text-muted-foreground">
                  {tr(
                    'Balances are cumulative through the as-of date at the bottom. They do not follow the sidebar fiscal-year filter.',
                    '結餘為截至頁尾日期的累計金額，不受側邊欄財政年度篩選影響。',
                    '结余为截至页尾日期的累计金额，不受侧边栏财政年度筛选影响。',
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Empty state */}
      {!hasAccounts && (
        <div className="bg-card border rounded-xl p-12 text-center space-y-4">
          <div className="flex justify-center">
            <BookOpen className="h-12 w-12 text-muted-foreground/50" />
          </div>
          <p className="text-sm text-muted-foreground">{tr(
            'No accounts yet. Start by using the Hong Kong industry template or build your COA manually.',
            '尚無科目。可使用香港行業模板或手動建立會計科目表。',
            '尚无科目。可使用香港行业模板或手动建立会计科目表。',
          )}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => seedMut.mutate()}
              disabled={seedMut.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              <Building2 className="h-4 w-4" />
              {seedMut.isPending ? tr('Seeding...', '正在建立...', '正在建立...') : tr('Use Industry Template', '使用行業模板', '使用行业模板')}
            </button>
            <button
              onClick={() => {
                setNewAccount({ account_code: '', account_name: '', account_type: 'asset', parent_code: '', opening_balance: 0 });
                setAddFormParent('__top__');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium hover:bg-muted/50"
            >
              <Plus className="h-4 w-4" />
              {tr('Build Manually', '手動建立', '手动建立')}
            </button>
          </div>
          {seedMut.isError && <p className="text-sm text-destructive">{(seedMut.error as Error).message}</p>}
          {seedMut.isSuccess && <p className="text-sm text-green-900 dark:text-green-200">{tr('COA seeded successfully!', '科目表建立成功！', '科目表建立成功！')}</p>}
        </div>
      )}

      {/* Sparse COA banner */}
      {hasAccounts && !missingLoading && missingCodes.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-100 px-4 py-3 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
              <div>
                <p className="text-sm font-semibold text-amber-950 dark:text-amber-50">
                  {tr('Missing transaction accounts', '缺少交易科目', '缺少交易科目')}
                </p>
                <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">
                  <span className="font-bold">{missingCodes.length}</span>{' '}
                  {tr(
                    'codes used by transactions are not in the Chart of Accounts. Review them before posting.',
                    '個交易使用的代碼尚未在會計科目表中。請在過賬前檢查。',
                    '个交易使用的代码尚未在会计科目表中。请在过账前检查。',
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowMissingModal(true)}
              className="px-4 py-2 text-sm font-semibold bg-amber-900 text-amber-50 rounded-lg hover:bg-amber-800 dark:bg-amber-300 dark:text-amber-950 dark:hover:bg-amber-200"
            >
              {tr('Review missing codes', '檢查缺少代碼', '检查缺少代码')}
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {hasAccounts && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={tr('Search code or name...', '搜尋代碼或名稱...', '搜索代码或名称...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">{tr('All Types', '所有類型', '所有类型')}</option>
            {TYPE_ORDER.map(t => (
              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
            ))}
          </select>
          <div className="flex-1" />
          <button
            onClick={handleRearrangeCodes}
            disabled={renumberMutation.isPending}
            className="px-3 py-1.5 text-sm border rounded-lg hover:bg-muted disabled:opacity-50"
          >
            {renumberMutation.isPending
              ? tr('Rearranging...', '重新排列中...', '重新排列中...')
              : tr('Re-arrange Code', '重新排列代碼', '重新排列代码')}
          </button>
          <label className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer select-none hover:bg-muted/50 transition-colors ${hideZeroBalance ? 'bg-primary/10 border-primary/30' : ''}`}>
            <input
              type="checkbox"
              checked={hideZeroBalance}
              onChange={e => setHideZeroBalance(e.target.checked)}
              className="sr-only"
            />
            <EyeOff className={`h-4 w-4 ${hideZeroBalance ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className="text-sm">{tr('Hide zero balance', '隱藏零餘額', '隐藏零余额')}</span>
          </label>
          <label className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer select-none hover:bg-muted/50 transition-colors ${showDisabled ? 'bg-primary/10 border-primary/30' : ''}`}>
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={e => setShowDisabled(e.target.checked)}
              className="sr-only"
            />
            <span className="text-sm">{showDisabled
              ? tr('Hide Disabled Accounts', '隱藏已停用科目', '隐藏已停用科目')
              : tr('Show Disabled Accounts', '顯示已停用科目', '显示已停用科目')}</span>
          </label>
        </div>
      )}

      {/* Inline add form — at top (global '+' button) */}
      {addFormParent === '__top__' && (
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">{tr('New Account', '新增科目', '新增科目')}</h3>
            <button onClick={() => setAddFormParent(null)} className="p-1 hover:bg-muted rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <input
              placeholder={tr('Code', '代碼', '代码')}
              value={newAccount.account_code}
              onChange={e => setNewAccount(p => ({ ...p, account_code: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              placeholder={tr('Account Name', '科目名稱', '科目名称')}
              value={newAccount.account_name}
              onChange={e => setNewAccount(p => ({ ...p, account_name: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <select
              value={newAccount.account_type}
              onChange={e => setNewAccount(p => ({ ...p, account_type: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {TYPE_ORDER.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
            <input
              placeholder={tr('Parent Code', '上級代碼', '上级代码')}
              value={newAccount.parent_code}
              onChange={e => setNewAccount(p => ({ ...p, parent_code: e.target.value }))}
              className="px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={() => { if (newAccount.account_code && newAccount.account_name) createMut.mutate(newAccount); }}
              disabled={!newAccount.account_code || !newAccount.account_name || createMut.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {createMut.isPending ? tr('Creating...', '建立中...', '建立中...') : tr('Create', '建立', '建立')}
            </button>
          </div>
          {createMut.isError && <p className="text-sm text-destructive mt-2">{(createMut.error as Error).message}</p>}
        </div>
      )}

      {/* No results */}
      {hasAccounts && filtered.length === 0 && (
        <div className="bg-card border rounded-xl p-12 text-center">
          <p className="text-sm text-muted-foreground">{tr('No accounts match your filters.', '沒有符合篩選條件的科目。', '没有符合筛选条件的科目。')}</p>
        </div>
      )}

      {/* COA section cards — each opens a hierarchy sub-page popup */}
      {typeOrder.filter(t => grouped[t]?.length > 0).map((type, idx) => (
        <div key={type} className="bg-card border rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setActiveCoaType(type)}
            aria-haspopup="dialog"
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, idx)}
            onDragEnd={handleDragEnd}
            className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${dragIndex === idx ? 'opacity-50 ring-2 ring-primary' : ''}`}
          >
            <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40 cursor-grab active:cursor-grabbing" />
            <span className={`inline-flex px-2.5 py-1 rounded text-sm font-semibold ${TYPE_COLORS[type]}`}>
              {TYPE_LABELS[type]}
            </span>
            <span className="text-sm text-muted-foreground">
              {grouped[type].length} {tr('accounts', '個科目', '个科目')}
            </span>
            {hasCurrentBalance && nonZeroByType[type] > 0 && (
              <span className="hidden text-sm text-green-700 dark:text-green-300 sm:inline">
                {nonZeroByType[type]} {tr('non-zero', '非零', '非零')}
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10">
              {tr('View accounts', '查看科目', '查看科目')}
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </button>
        </div>
      ))}

      {activeCoaType && grouped[activeCoaType] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={() => setActiveCoaType(null)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="coa-hierarchy-title"
            className="flex h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b bg-muted/20 p-5">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {tr('Chart of Accounts hierarchy', '會計科目表階層', '会计科目表阶层')}
                </p>
                <h3 id="coa-hierarchy-title" className="mt-1 text-xl font-bold text-foreground">
                  {TYPE_LABELS[activeCoaType]}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {grouped[activeCoaType].length} {tr('accounts shown in code-level hierarchy', '個科目以代碼層級顯示', '个科目以代码层级显示')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveCoaType(null)}
                className="rounded-lg border bg-background p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={tr('Close', '關閉', '关闭')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground shadow-[0_1px_0_hsl(var(--border))]">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium">{tr('Code / Account', '代碼 / 科目', '代码 / 科目')}</th>
                    <th className="px-4 py-3 text-right font-medium">{tr('Balance b/f', '承前結餘', '承前结余')}</th>
                    {hasCurrentBalance && <th className="px-4 py-3 text-right font-medium">{tr('Balance', '結餘', '结余')}</th>}
                    <th className="px-5 py-3 text-right font-medium">{tr('Status / actions', '狀態 / 操作', '状态 / 操作')}</th>
                  </tr>
                </thead>
                <tbody>
                  {buildCoaTree(grouped[activeCoaType]).map(({ account: a, depth, isParent }) => {
                    const level = Math.min(depth, 4);
                    const levelShading = [
                      'bg-muted/80',
                      'bg-muted/50',
                      'bg-muted/25',
                      'bg-background',
                      'bg-muted/10',
                    ][level];
                    const editing = editingName?.code === a.account_code ? editingName : null;
                    const editingOpening = editingOpeningState?.code === a.account_code ? editingOpeningState : null;
                    const isActive = a.is_active !== 0;
                    const openingDraftInvalid = !!editingOpening && (
                      editingOpening.value.trim() !== '' && !Number.isFinite(Number(editingOpening.value))
                    );

                    return (
                      <tr key={`${a.id || a.account_code}-${depth}`} className={`border-b border-border/60 ${levelShading}`}>
                        <td className="px-5 py-3" style={{ paddingLeft: `${20 + level * 28}px` }}>
                          <div className="flex items-start gap-3">
                            <span className={`mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${
                              isParent ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                            }`}>
                              L{level + 1}
                            </span>
                            <div className="min-w-0">
                              <div className={`font-mono ${isParent ? 'text-sm font-bold text-foreground' : 'text-xs text-foreground'}`}>
                                {a.account_code}
                              </div>
                              {editing ? (
                                <div className="mt-1 flex items-center gap-1" onClick={event => event.stopPropagation()}>
                                  <input
                                    value={editing.value}
                                    onChange={event => setEditingName({ code: a.account_code, value: event.target.value })}
                                    onKeyDown={event => {
                                      if (event.key === 'Enter' && !updateNameMut.isPending) {
                                        updateNameMut.mutate({ code: a.account_code, account_name: editing.value });
                                      }
                                      if (event.key === 'Escape') setEditingName(null);
                                    }}
                                    autoFocus
                                    className="w-full max-w-72 rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                  />
                                  <button
                                    onClick={() => updateNameMut.mutate({ code: a.account_code, account_name: editing.value })}
                                    disabled={!editing.value.trim() || updateNameMut.isPending}
                                    title={tr('Save', '儲存', '保存')}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded bg-primary text-primary-foreground disabled:opacity-50"
                                  >
                                    <Check className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => setEditingName(null)}
                                    title={tr('Cancel', '取消', '取消')}
                                    className="inline-flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:bg-muted"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <p className={`min-w-0 truncate ${isParent ? 'text-sm font-semibold text-foreground' : 'text-sm text-foreground'}`}>
                                    {a.account_name}
                                  </p>
                                  <button
                                    onClick={() => setEditingName({ code: a.account_code, value: a.account_name || '' })}
                                    title={tr('Edit name', '編輯名稱', '编辑名称')}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                  {isParent && (
                                    <button
                                      onClick={() => handleAddChild(a.account_code, a.account_type)}
                                      title={tr('Add sub-account', '新增子科目', '新增子科目')}
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {editingOpening ? (
                            <div
                              className="inline-flex items-center gap-1"
                              onClick={event => event.stopPropagation()}
                            >
                              <input
                                value={editingOpening.value}
                                onChange={event => setEditingOpeningState({
                                  code: a.account_code,
                                  value: event.target.value,
                                })}
                                onKeyDown={event => {
                                  if (event.key === 'Enter' && !openingDraftInvalid && !updateOpeningMut.isPending) {
                                    updateOpeningMut.mutate({
                                      code: a.account_code,
                                      opening_balance: editingOpening.value.trim() === ''
                                        ? 0
                                        : Number(editingOpening.value),
                                    });
                                  }
                                  if (event.key === 'Escape') setEditingOpeningState(null);
                                }}
                                type="text"
                                inputMode="decimal"
                                aria-invalid={openingDraftInvalid}
                                aria-label={`${a.account_code} ${tr('opening balance', '期初結餘', '期初结余')}`}
                                title={openingDraftInvalid
                                  ? tr('Enter a numeric amount', '請輸入數字', '请输入数字')
                                  : tr('Opening balance', '期初結餘', '期初结余')}
                                autoFocus
                                className={`w-28 rounded border px-2 py-1 text-right font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring ${
                                  openingDraftInvalid ? 'border-red-500' : 'border-input'
                                }`}
                              />
                              <button
                                onClick={() => updateOpeningMut.mutate({
                                  code: a.account_code,
                                  opening_balance: editingOpening.value.trim() === ''
                                    ? 0
                                    : Number(editingOpening.value),
                                })}
                                disabled={openingDraftInvalid || updateOpeningMut.isPending}
                                title={openingDraftInvalid
                                  ? tr('Enter a numeric amount', '請輸入數字', '请输入数字')
                                  : tr('Save', '儲存', '保存')}
                                className="inline-flex h-7 w-7 items-center justify-center rounded bg-primary text-primary-foreground disabled:opacity-50"
                              >
                                <Check className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => setEditingOpeningState(null)}
                                disabled={updateOpeningMut.isPending}
                                title={tr('Cancel', '取消', '取消')}
                                className="inline-flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:bg-muted disabled:opacity-50"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <span className="font-mono text-xs">{formatBalance(a.opening_balance)}</span>
                              {isParent ? (
                                <span
                                  title={tr(
                                    'Parent accounts roll up sub-accounts; set opening balances on leaf accounts only.',
                                    '母科目會彙總子科目；期初結餘只能在葉子科目上設定。',
                                    '母科目会汇总子科目；期初结余只能在叶子科目上设置。',
                                  )}
                                  className="inline-flex h-6 w-6 items-center justify-center text-muted-foreground/60"
                                  role="img"
                                  aria-label={tr(
                                    'Parent account: opening balance is read-only',
                                    '母科目：期初結餘不可編輯',
                                    '母科目：期初结余不可编辑',
                                  )}
                                >
                                  <Info className="h-3 w-3" />
                                </span>
                              ) : (
                                <button
                                  onClick={() => setEditingOpeningState({
                                    code: a.account_code,
                                    value: String(a.opening_balance ?? ''),
                                  })}
                                  title={tr('Edit opening balance', '編輯期初結餘', '编辑期初结余')}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        {hasCurrentBalance && (
                          <td className="px-4 py-3 text-right font-mono text-xs font-semibold">{formatBalance(a.current_balance)}</td>
                        )}
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs ${
                              isActive ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100' : 'bg-muted text-muted-foreground'
                            }`}>
                              {isActive ? tr('Active', '啟用', '启用') : tr('Disabled', '停用', '停用')}
                            </span>
                            <button
                              onClick={() => updateStatusMut.mutate({ code: a.account_code, is_active: isActive ? 0 : 1 })}
                              className="text-xs text-primary hover:underline"
                            >
                              {isActive ? tr('Disable', '停用', '停用') : tr('Enable', '啟用', '启用')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      )}

            <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{filtered.length} / {accounts.length} {tr('accounts', '個科目', '个科目')}</span>
        {queryAsOf && <span>{tr(`Balances as of ${queryAsOf}`, `結餘截至 ${queryAsOf}`, `结余截至 ${queryAsOf}`)}</span>}
      </div>

      <ConfirmDialog
        show={!!confirmDisable}
        title={tr('Disable Account', '停用科目', '停用科目')}
        message={`${confirmDisable?.code} — ${confirmDisable?.name}\n\n${tr(
          'This account cannot be used in new journal entries, but its transaction history is preserved.',
          '此科目不能再用於新日記帳分錄，但交易記錄將保留。',
          '此科目不能再用于新日记账分录，但交易记录将保留。',
        )}`}
        confirmLabel={tr('Disable', '停用', '停用')}
        danger
        onConfirm={() => confirmDisable && updateStatusMut.mutate({ code: confirmDisable.code, is_active: 0 })}
        onCancel={() => setConfirmDisable(null)}
      />

      {showMissingModal && (
        <MissingCodesModal onClose={() => {
          setShowMissingModal(false);
          queryClient.invalidateQueries({ queryKey: ['missing-codes'] });
        }} />
      )}
    </div>
  );
}
